import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { stylingHelpers } from '../../designTokens';

import { uniqueId } from '../lib/id';
import Label from './Label';
import Icon from './Icon';
import ValidationHint from './ValidationHint';

const { textMega } = stylingHelpers;

const containerStyles = ({ theme }) => css`
  position: relative;
  label &,
  label + & {
    margin-top: ${theme.spacings.bit};
  }
`;

const InputContainer = styled('div')(containerStyles);

const labelCustomStyles = ({ theme, noMargin }) =>
  !noMargin &&
  css`
    margin-bottom: ${theme.spacings.mega};
  `;

const InputLabel = styled(Label)(labelCustomStyles);

const inputBaseStyles = ({ theme, minWidth }) => css`
  -webkit-appearance: none;
  font-family: ${theme.fontStack.default};
  background-color: ${theme.colors.white};
  border: none;
  outline: 0;
  border-radius: ${theme.borderRadius.mega};
  padding: calc(${theme.spacings.kilo} + 1px) ${theme.spacings.kilo};
  transition: box-shadow ${theme.transitions.default}, padding ${theme.transitions.default};
  width: 100%;
  min-width: ${minWidth ? `${minWidth}px` : '250px'};
  margin: 0;
  box-shadow: 0 0 0 1px ${theme.colors.n400};
  ${textMega({ theme })};
  &::placeholder {
    color: ${theme.colors.n900};
    transition: color ${theme.transitions.default};
  }
`;

const inputWarningStyles = ({ theme, hasWarning, disabled }) =>
  !disabled &&
  hasWarning &&
  css`
    &:not(:focus)::placeholder {
      color: ${theme.colors.y500};
    }
  `;

const inputInvalidStyles = ({ theme, invalid, disabled }) =>
  !disabled &&
  invalid &&
  css`
    box-shadow: 0 0 0 1px ${theme.colors.r500};
    &:not(:focus)::placeholder {
      color: ${theme.colors.r500};
    }
  `;

const inputReadonlyStyles = ({ theme, readOnly }) =>
  readOnly &&
  css`
    background-color: ${theme.colors.n200};
  `;

const inputDisabledStyles = ({ theme, disabled }) =>
  disabled &&
  css`
    background-color: ${theme.colors.n300};
  `;

const inputTextAlignRightStyles = ({ textAlign }) =>
  textAlign === 'right' &&
  css`
    text-align: right;
  `;

const inputPrefixStyles = ({ hasPrefix }) =>
  hasPrefix &&
  css`
    padding-left: 40px;
  `;

const inputSuffixStyles = ({ hasSuffix }) =>
  hasSuffix &&
  css`
    padding-right: 40px;
  `;

const InputElement = styled('input')(
  inputBaseStyles,
  inputWarningStyles,
  inputInvalidStyles,
  inputTextAlignRightStyles,
  inputReadonlyStyles,
  inputDisabledStyles,
  inputPrefixStyles,
  inputSuffixStyles,
);

const prefixStyles = ({ theme }) => css`
  position: absolute;
  top: 1px;
  left: 1px;
  pointer-events: none;
  color: ${theme.colors.n700};
  padding: ${theme.spacings.kilo};
  height: ${theme.spacings.peta};
  width: ${theme.spacings.peta};
`;

const suffixStyles = ({ theme, hasValidationIcon }) => css`
  position: absolute;
  top: 1px;
  right: ${hasValidationIcon ? '29px' : '1px'};
  pointer-events: none;
  color: ${theme.colors.n700};
  padding: ${theme.spacings.kilo};
  height: ${theme.spacings.peta};
  width: ${theme.spacings.peta};
  transition: right ${theme.transitions.default};
`;

const iconBaseStyles = ({ theme, color }) => css`
  position: absolute;
  right: 0;
  margin-top: 0.3em;
  margin-right: 0.3em;
  color: ${theme.colors[color]};
  font-size: 2em;
`;

const StyledIcon = styled(Icon)(iconBaseStyles);

const getIcon = (state) => {
  switch (true) {
    case state.disabled: {
      return null;
    }
    case state.invalid: {
      return <StyledIcon role='presentation' icon='exclamation-circle' color='danger' />;
    }
    case state.hasWarning: {
      return <StyledIcon role='presentation' icon='exclamation-circle' color='warning' />;
    }
    case state.showValid: {
      return <StyledIcon role='presentation' prefix='far' icon='check-circle' color='success' />;
    }
    default: {
      return null;
    }
  }
};

const Input = React.forwardRef((state, ref) => {
  const {
    value,
    renderPrefix: RenderPrefix,
    renderSuffix: RenderSuffix,
    validationHint,
    invalid,
    hasWarning,
    showValid,
    noMargin,
    inline,
    disabled,
    labelStyles,
    inputStyles,
    label,
    hideLabel,
    validationTextAlign,
    id: customId,
    maxLength,
    type,
    minWidth,
    ...props
  } = state;
  const id = customId || uniqueId('input_');
  const hintId = 'hint_' + id;

  const prefix = RenderPrefix && <RenderPrefix css={prefixStyles} />;
  const suffix = RenderSuffix && <RenderSuffix css={suffixStyles} />;

  const hasPrefix = Boolean(prefix);
  const hasSuffix = Boolean(suffix);

  const icon = getIcon(state);

  return (
    <InputLabel htmlFor={id} inline={inline} disabled={disabled} noMargin={noMargin}>
      <InputContainer css={labelStyles}>
        {prefix}
        <InputElement
          minWidth={minWidth}
          type={type}
          id={id}
          value={value}
          invalid={invalid}
          aria-invalid={invalid}
          aria-describedby={hintId}
          disabled={disabled}
          hasWarning={hasWarning}
          hasPrefix={hasPrefix}
          hasSuffix={hasSuffix}
          css={inputStyles}
          maxLength={maxLength}
          ref={ref}
          {...props}
        />
        {icon}
        {suffix}
      </InputContainer>
      <ValidationHint
        id={hintId}
        disabled={disabled}
        invalid={invalid}
        hasWarning={hasWarning}
        showValid={showValid}
        validationHint={validationHint}
        textAlign={validationTextAlign}
      />
    </InputLabel>
  );
});

Input.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  renderPrefix: PropTypes.node,
  renderSuffix: PropTypes.node,
  validationHint: PropTypes.string,
  invalid: PropTypes.bool,
  hasWarning: PropTypes.bool,
  showValid: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  noMargin: PropTypes.bool,
  textAlign: PropTypes.oneOf(['left', 'right']),
  hideLabel: PropTypes.bool,
  inputStyles: PropTypes.object,
  labelStyles: PropTypes.object,
  validationTextAlign: PropTypes.oneOf(['left', 'right']),
  maxLength: PropTypes.string,
  type: PropTypes.oneOf(['text', 'textarea']),
};

export default Input;
